import React, { useState, useMemo } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Tooltip } from "@mui/material";

import { ToolTipIcon } from "icons";

const FeeTransparencyComponent = ({
  totalPrice = null,
  baseRent = null,
  leaseTerm = null,
  fees = [],
  unitBedrooms = null,
  unitArea = null,
  availabilityDate = null,
  hidePricing = false,
  tmlpEnabled,
  autoExpand = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(autoExpand);

  const transformedFees = useMemo(() => {
    return fees
      .filter((f) => f.is_enabled !== false)
      .map((fee) => ({
        name: fee.label,
        required: fee.is_required === true,
        frequency: fee.frequency === "monthly" ? "Monthly" : fee.frequency === "one_time" ? "One Time" : "Per Occurrence",
        price: fee.min_amount === "" || fee.min_amount === null ? "Usage-Based" : fee.min_amount,
        tooltip: fee.tooltip || null,
      }));
  }, [fees]);

  const requiredMonthly = useMemo(() => transformedFees.filter((f) => f.required && f.frequency === "Monthly"), [transformedFees]);
  const requiredOneTime = useMemo(() => transformedFees.filter((f) => f.required && f.frequency === "One Time"), [transformedFees]);
  const optionalMonthly = useMemo(() => transformedFees.filter((f) => !f.required && f.frequency === "Monthly"), [transformedFees]);

  const optionalOneTime = useMemo(
    () => transformedFees.filter((f) => !f.required && (f.frequency === "One Time" || f.frequency === "Per Occurrence")),
    [transformedFees]
  );

  const formatPrice = (price) => {
    if (typeof price === "number") {
      return `$${price.toFixed(2)}`;
    }

    if (typeof price === "string" && price.startsWith("$")) {
      return price;
    }

    return price;
  };

  const FeeRow = ({ fee, index }) => (
    <div
      className="hayley-flex hayley-items-center hayley-justify-between hayley-px-2 hayley-py-1.5"
      style={{ backgroundColor: index % 2 === 0 ? "#f2f2f2" : "#ffffff" }}
    >
      <div className="hayley-flex hayley-items-center hayley-gap-1 hayley-pr-2">
        <span className="hayley-max-w-[200px] hayley-truncate hayley-text-sm" style={{ color: "#333333" }}>
          {fee.name}
        </span>
        {fee.tooltip && (
          <Tooltip
            title={fee.tooltip}
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "#333333",
                  color: "#ffffff",
                  fontSize: "14px",
                  fontFamily: "Open Sans",
                  fontWeight: 400,
                  padding: "6px 10px",
                  lineHeight: "20px",
                  "& .MuiTooltip-arrow": {
                    color: "#333333",
                  },
                },
              },
            }}
          >
            <div className="hayley-flex hayley-cursor-pointer hayley-items-center">
              <ToolTipIcon classes="hayley-h-4 hayley-w-4" fill="#666666" />
            </div>
          </Tooltip>
        )}
      </div>
      <span className="hayley-whitespace-nowrap hayley-text-xs hayley-font-semibold" style={{ color: "#0d71b9" }}>
        {formatPrice(fee.price)}
      </span>
    </div>
  );

  const FeeSection = ({ title, fees, startIndex = 0 }) => (
    <div className="hayley-mb-0">
      <div className="hayley-px-2 hayley-py-1.5 hayley-text-xs hayley-font-semibold hayley-text-white" style={{ backgroundColor: "#0d71b9" }}>
        {title}
      </div>
      <div>
        {fees.map((fee, index) => (
          <FeeRow key={`${title}-${index}`} fee={fee} index={startIndex + index} />
        ))}
      </div>
    </div>
  );

  if (hidePricing) {
    return null;
  }

  if (!totalPrice && !baseRent) {
    if (transformedFees?.length === 0) {
      return null;
    }
  }

  return (
    <div className="hayley-w-full hayley-bg-white hayley-py-4">
      {(!autoExpand || totalPrice || baseRent || leaseTerm) && (
        <div className="hayley-mb-3 hayley-px-3 hayley-pb-3" style={{ borderBottom: "1px solid #f2f2f2" }}>
          <div className="hayley-flex hayley-items-end hayley-justify-between">
            <div>
              {tmlpEnabled && totalPrice && (
                <div className="hayley-flex hayley-items-baseline">
                  <span className="hayley-whitespace-nowrap hayley-text-2xl hayley-font-bold" style={{ color: "#0d71b9" }}>
                    $ {typeof totalPrice === "number" ? totalPrice.toLocaleString() : totalPrice}
                  </span>
                  <span className="hayley-ml-2 hayley-whitespace-nowrap hayley-text-xs hayley-font-semibold" style={{ color: "#aaaaaa" }}>
                    Total monthly leasing price
                  </span>
                </div>
              )}
              {baseRent && (
                <p className="hayley-mt-0.5 hayley-text-xs hayley-font-semibold" style={{ color: "#666666" }}>
                  Base Rent from ${typeof baseRent === "number" ? baseRent.toLocaleString() : baseRent}
                </p>
              )}
              {leaseTerm && (
                <p className="hayley-text-xs hayley-font-semibold" style={{ color: "#333333" }}>
                  Based on {leaseTerm}
                </p>
              )}
            </div>

            {!autoExpand && transformedFees.length > 0 && (
              <div
                onClick={() => setIsExpanded(!isExpanded)}
                className="hayley-flex hayley-cursor-pointer hayley-items-center hayley-whitespace-nowrap hayley-pb-1 hayley-text-xs hayley-font-semibold hayley-underline"
                style={{ color: "#26bbed" }}
              >
                <span>Fee summary</span>
                {isExpanded ? <ChevronUp className="hayley-ml-0.5 hayley-h-3 hayley-w-3" /> : <ChevronDown className="hayley-ml-0.5 hayley-h-3 hayley-w-3" />}
              </div>
            )}
          </div>
        </div>
      )}

      {(autoExpand || isExpanded) && transformedFees.length > 0 && (
        <div
          className={`hayley-max-h-[803px] hayley-overflow-y-auto ${
            !autoExpand && (unitBedrooms || unitArea || availabilityDate) ? "hayley-mb-3 hayley-pb-3" : ""
          }`}
          style={!autoExpand && (unitBedrooms || unitArea || availabilityDate) ? { borderBottom: "1px solid #f2f2f2" } : {}}
        >
          {/* Required Fees - Monthly */}
          {requiredMonthly.length > 0 && <FeeSection title="Required Fees - Monthly" fees={requiredMonthly} startIndex={0} />}

          {/* Required Fees - One Time */}
          {requiredOneTime.length > 0 && <FeeSection title="Required Fees - One Time" fees={requiredOneTime} startIndex={requiredMonthly.length} />}

          {/* Optional Fees - Monthly */}
          {optionalMonthly.length > 0 && <FeeSection title="Optional Fees - Monthly" fees={optionalMonthly} startIndex={0} />}

          {/* Optional Fees - One Time */}
          {optionalOneTime.length > 0 && <FeeSection title="Optional Fees - One Time" fees={optionalOneTime} startIndex={0} />}
        </div>
      )}

      {/* Property Details Section */}
      {(unitBedrooms || unitArea || availabilityDate) && (
        <div className="hayley-space-y-3 hayley-px-3">
          <div className="hayley-flex hayley-justify-between">
            {unitBedrooms && (
              <div className="hayley-flex-1">
                <p className="hayley-text-base hayley-font-semibold" style={{ color: "#333333" }}>
                  {unitBedrooms}
                </p>
                <p className="hayley-text-xs" style={{ color: "#aaaaaa" }}>
                  Bed
                </p>
              </div>
            )}
            {unitArea && (
              <div className="hayley-flex-1">
                <p className="hayley-text-base hayley-font-semibold" style={{ color: "#333333" }}>
                  {unitArea}
                </p>
                <p className="hayley-text-xs" style={{ color: "#aaaaaa" }}>
                  Min.Sq.ft
                </p>
              </div>
            )}
            {availabilityDate && (
              <div className="hayley-flex-1">
                <p className="hayley-text-base hayley-font-semibold" style={{ color: "#333333" }}>
                  {availabilityDate}
                </p>
                <p className="hayley-text-xs" style={{ color: "#aaaaaa" }}>
                  Available from
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeTransparencyComponent;
